import type { z as Z } from 'zod';

import { z } from 'zod';

import { CHARACTER_ROLES, KEBAB, nodeFields } from './storyData.js';

export {
  GAMESHOW_CORRECT_DELTA,
  GAMESHOW_QUIZ_ACTIVITY_TYPES,
  GAMESHOW_ROUND_COUNT,
} from './gameshowConstants.js';

export const GameshowCharacter = z.looseObject({
  id: z.string().regex(KEBAB),
  role: z.enum(CHARACTER_ROLES),
  name: z.string().min(1),
  title: z.string().min(1),
  contentAssetSlug: z.string().min(1),
  // Resolved server-side from `contentAssetSlug`, which a gameshow character has to name.
  imageUrl: z.string().optional(),
});

export const GameshowScoreDefinition = z.looseObject({
  scoreName: z.string().min(1),
  displayName: z.string().min(1),
  characterId: z.string().min(1),
  initialValue: z.number(),
});

const NarratorDialogueLine = z.looseObject({
  dialogue: z.string().min(1),
});

const CharacterDialogueLine = z.looseObject({
  name: z.string().min(1),
  dialogue: z.string().min(1),
});

const ScoreDelta = z.looseObject({
  scoreName: z.string().min(1),
  characterId: z.string().min(1),
  delta: z.number(),
});

const OutcomeEffect = z.looseObject({
  scoreDeltas: z.array(ScoreDelta).optional(),
  stateFlags: z.array(z.string()).optional(),
  stateFlagReasons: z.record(z.string(), z.string()).optional(),
});

const EndingSummary = z.looseObject({
  summaryText: z.string().min(1),
  showGameStats: z.boolean().optional(),
});

export const GameshowNode = z.looseObject({
  ...nodeFields,
  id: z.string().min(1),
  internalName: z.string().optional(),
  narratorDialogue: z.array(NarratorDialogueLine).optional(),
  characterDialogue: z.array(CharacterDialogueLine).optional(),
  scoreUpdateNarratorDialogue: z.array(NarratorDialogueLine).optional(),
  showScoreUpdates: z.enum(['all', 'character', 'global', 'none']).optional(),
  outcomeEffects: z.record(z.string(), OutcomeEffect).optional(),
  completeStateFlagReason: z.string().optional(),
  endingSummary: EndingSummary.optional(),
  backgroundContentAssetSlug: z.string().optional(),
  backgroundImageUrl: z.string().optional(),
});

/** The inner `storyData` of a gameshow: a host, a contestant, scored rounds. */
export const Gameshow = z.looseObject({
  id: z.string().regex(KEBAB),
  title: z.string().min(1),
  startNodeId: z.string().min(1),
  // The server fills this from the slug below, which is the one a gameshow has to name.
  defaultBackgroundImageUrl: z.string().min(1).optional(),
  defaultBackgroundContentAssetSlug: z.string().min(1),
  defaultBackgroundMobileContentAssetSlug: z.string().min(1),
  narratorName: z.string().min(1),
  // Optional since CP-10731: base content leaves the host out and the player shows Blinky.
  narratorContentAssetSlug: z.string().min(1).optional(),
  narratorSpriteUrl: z.string().min(1).optional(),
  longestPathLength: z.number().optional(),
  scoreDefinitions: z.array(GameshowScoreDefinition).min(1),
  characters: z.array(GameshowCharacter).min(1),
  nodes: z.array(GameshowNode).min(1),
});

export type Gameshow = Z.infer<typeof Gameshow>;
export type GameshowCharacter = Z.infer<typeof GameshowCharacter>;
export type GameshowScoreDefinition = Z.infer<typeof GameshowScoreDefinition>;
export type GameshowNode = Z.infer<typeof GameshowNode>;
